import React, { useState, useMemo, useRef, useEffect } from "react";
import {
  Dialog,
  Box,
  Typography,
  IconButton,
  Autocomplete,
  TextField,
  CircularProgress,
  Avatar,
  Tooltip,
  Divider,
  Checkbox,
  Menu,
  MenuItem,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import UserInteractionsService from "../../services/interactions/Interactions";
import useDisplaySnackbar from "../../utils/useDisplaySnackbar";
import { getUserAttributes } from "../../utils/auth";
import {
  getAvatarInitials,
  getAvatarColor,
  validateEmail,
  isExcludedDomain,
  getFullEmailDomain,
  aiSupportedExtensions,
  getFileExtension,
  isGroupAiCompleted,
  getGroupStatusMap,
  secondsToHHMMSS,
  assetCategory
} from "../../utils/utils";
import ProgressSpinner from "../Common/ProgressSpinner";
import ExpiryIcon from "../../assets/interaction-detail-page/expiry.svg";
import callRecordingIcon from "../../assets/interaction-detail-page/idp-call-recording.svg";
import copyLink from "../../assets/interaction-detail-page/idp-copy-link.svg";
import useGlobalContext from "../../context/useGlobalContext";
import RestrictIcon from "../../assets/interaction-detail-page/share-interaction-restrict-icon.svg";
import "../../styles/interactionDetailsPage.css";
import InteractionIcon from "../../assets/meeting/interaction-icon.svg";
import ClipIcon from "../../assets/interaction-detail-page/clip-tab-icon.svg";
import expandIcon from "../../assets/interaction-detail-page/expand-share-icon.svg";
import sharedVideoIcon from "../../assets/interaction-detail-page/share-video-icon.svg";

interface UserOption {
  name: string;
  email: string;
  isParticipant?: boolean;
  accountId?: string;
}
interface AccessPerson {
  key: string;
  kind: "pending" | "shared";
  email: string;
  name: string;
  pictureUrl?: string;
  user?: any;
  isExternal: boolean;
  domain: string;
}

interface ShareInteractionDialogProps {
  open: boolean;
  onClose: () => void;
  interactionId: string;
  interactionTitle?: string;
  collaterals?: any[];
  initialTab?: "recording" | "clips";
}

const formatClipTime = (value: unknown): string => {
  if (typeof value === "number") return secondsToHHMMSS(value).replace(/^00:/, "");
  if (typeof value !== "string") return "";
  return value.replace(/^00:/, "");
};
const isGeneratedClipAsset = (collateral: any) =>
  collateral?.assetSource?.toLowerCase() === "7g_video_clip";

const ShareInteractionDialog: React.FC<ShareInteractionDialogProps> = ({
  open,
  onClose,
  interactionId,
  collaterals = [],
  initialTab = "recording",
}) => {
  const { showSnackbar } = useDisplaySnackbar();
  const { accountDetails } = useGlobalContext();
  const loggedInAccountId = accountDetails?.accountId;
  const [emails, setEmails] = useState<string[]>([]);
  const [inputValue, setInputValue] = useState("");
  const [isSending, setIsSending] = useState(false);
  const [focused, setFocused] = useState(false);
  const [emailError, setEmailError] = useState("");
  const [shareUrl, setShareUrl] = useState<string>("");
  const [tokenId, setTokenId] = useState<string>("");
  const [showSpinner, setShowSpinner] = useState<boolean>(false);
  const [selectedContentTab, setSelectedContentTab] = useState<string>(initialTab);
  const [selectMenuAnchor, setSelectMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedClipId, setSelectedClipId] = useState<string>("");
  const [selectedRecordingId, setSelectedRecordingId] = useState<string>("");
  const hasClips = collaterals.some(
    (collateral) => collateral?.assetSource?.toLowerCase() === "7g_video_clip" && collateral?.assetType?.toLowerCase() === "video"
  );
  const clipOptions = useMemo(() => collaterals
    .filter((collateral) => collateral?.assetSource?.toLowerCase() === "7g_video_clip" && collateral?.assetType?.toLowerCase() === "video")
    .map((collateral, index) => {
      const metadata = collateral?.assetMetaData || {};
      const start = metadata.start_time;
      const end = metadata.end_time;
      const timeRange = start != null && end != null
        ? `${formatClipTime(start)} - ${formatClipTime(end)}`
        : "";
      const assetName =
        (collateral?.assetName || `Clip ${index + 1}`).split("/").pop() || `Clip ${index + 1}`;
      const name = assetName.replace(/_video_clip\.mp4$/i, "");
      const createdBy = collateral?.uploadedBy;
      return {
        id: String(collateral?.interactionAssetId || `${name}-${index}`),
        name,
        timeRange,
        createdBy,
      };
    }), [collaterals]);
  const recordingTabs = useMemo(() => {
    const groups = new Map<string, any[]>();
    collaterals.filter((collateral) => !isGeneratedClipAsset(collateral)).forEach((collateral) => {
      const groupId = collateral?.interactionGroupId;
      if (!groupId) return;
      groups.set(groupId, [...(groups.get(groupId) || []), collateral]);
    });

    let audioCount = 0;
    let videoCount = 0;
    const groupStatusMap = getGroupStatusMap(collaterals);
    const entries = [...groups.entries()].filter(([groupId, items]) => {
      const hasAccountAsset = items.some((item) => item?.assetSource?.toLowerCase() === "account");
      return !hasAccountAsset || isGroupAiCompleted(groupStatusMap[groupId] ?? []);
    });
    const isRecordingGroup = (items: any[]) => items.some((item) => {
      const source = item?.assetSource?.toLowerCase();
      return source === "7g_recall" || source === "zoom" ||
        (item?.assetType?.toLowerCase() === "audio" && source === "7g_mobile_app") ||
        (item?.assetName?.toLowerCase().endsWith(".mp4") && source !== "account");
    });
    entries.sort(([, itemsA], [, itemsB]) => Number(isRecordingGroup(itemsB)) - Number(isRecordingGroup(itemsA)));

    const totalAudio = entries.filter(([, items]) => items.some((item) =>
      item?.assetType?.toLowerCase() === "audio" && item?.assetSource?.toLowerCase() === "7g_mobile_app"
    )).length;
    const totalVideo = entries.filter(([, items]) => items.some((item) =>
      item?.assetName?.toLowerCase().endsWith(".mp4") &&
      ["7g_recall", "zoom"].includes(item?.assetSource?.toLowerCase())
    )).length;

    return entries.map(([groupId, items], index) => {
      const isAudio = items.some((item) =>
        item?.assetType?.toLowerCase() === "audio" && item?.assetSource?.toLowerCase() === "7g_mobile_app"
      );
      const accountAsset = items.find((item) => {
        const extension = getFileExtension(item?.assetName || "").toLowerCase();
        return item?.assetSource?.toLowerCase() === "account" &&
          item?.assetName && aiSupportedExtensions.includes(extension);
      });

      let label: string;
      if (accountAsset) {
        label = accountAsset.assetName;
      } else if (isAudio) {
        audioCount += 1;
        label = totalAudio > 1 ? `Audio Recording ${audioCount}` : "Audio Recording";
      } else {
        videoCount += 1;
        label = totalVideo > 1 ? `Recording ${videoCount}` : `Recording ${index + 1}`;
      }
      const duration = items.find((item) => typeof item?.assetMetaData?.transcriptDuration === "number")
        ?.assetMetaData?.transcriptDuration ?? null;
      return { groupId, label, duration };
    });
  }, [collaterals]);
  const isClipTab = selectedContentTab === "clips";
  const selectOptions = isClipTab
    ? clipOptions.map((clip) => ({
        id: clip.id,
        label: clip.name,
        detail: clip.timeRange,
        createdBy: clip.createdBy,
      }))
    : recordingTabs.map((recording) => ({
        id: recording.groupId,
        label: recording.label,
        detail: typeof recording.duration === "number"
          ? secondsToHHMMSS(recording.duration).replace(/^00:/, "")
          : "",
        createdBy: "",
      }));
  const selectedOptionId = isClipTab ? selectedClipId : selectedRecordingId;
  const selectedOption = selectOptions.find((option) => option.id === selectedOptionId);
  const showSelectOptions = selectOptions.length > 0;
  const getSelectedSharedAssetIds = () => {
    const selectedClip = isClipTab
      ? collaterals.find((collateral) =>
          String(collateral?.interactionAssetId || collateral?.assetId) === selectedClipId
        )
      : undefined;
    const selectedGroupId = isClipTab
      ? selectedClip?.interactionGroupId
      : selectedRecordingId;
    const groupAssets = selectedGroupId
      ? collaterals.filter((collateral) =>
          collateral?.interactionGroupId === selectedGroupId &&
          (isClipTab || !isGeneratedClipAsset(collateral))
        )
      : [];
    const getFileName = (asset: any) =>
      (asset?.assetName || asset?.assetLocation || "").split("/").pop()?.toLowerCase() || "";
    const clipId = selectedClip?.assetMetaData?.clip_id;
    const clipAssets = clipId != null
      ? collaterals.filter((collateral) =>
          String(collateral?.assetMetaData?.clip_id) === String(clipId)
        )
      : [];
    const selectedVideo = isClipTab
      ? clipAssets.find((asset) => getFileName(asset).endsWith(".mp4")) ||
        (getFileName(selectedClip).endsWith(".mp4") ? selectedClip : undefined)
      : groupAssets.find((asset) => getFileName(asset).endsWith(".mp4"));
    const selectedTranscriptAssets = isClipTab ? clipAssets : groupAssets;
    const condensedTranscript = selectedTranscriptAssets.find((asset) =>
      asset?.assetType?.toLowerCase() === "transcript" &&
      getFileName(asset).endsWith("_condensed_transcript.txt")
    );
    const relatedTranscript = condensedTranscript || selectedTranscriptAssets.find((asset) =>
      asset?.assetType?.toLowerCase() === "transcript" && getFileName(asset).endsWith(".txt")
    );

    return [...new Set([
      selectedVideo?.interactionAssetId || selectedVideo?.assetId,
      relatedTranscript?.interactionAssetId || relatedTranscript?.assetId,
    ].filter((assetId): assetId is string => typeof assetId === "string" && !!assetId))];
  };
  const hasUnselectedMultipleOptions = selectOptions.length > 1 && !selectedOption;
  const selectedSharedAssetIds = useMemo(
    () => getSelectedSharedAssetIds(),
    [isClipTab, selectedClipId, selectedRecordingId, collaterals]
  );
  const canLoadSelectedAssetSharingData =
    open &&
    !!interactionId &&
    selectedSharedAssetIds.length > 0 &&
    !hasUnselectedMultipleOptions;
  const keepInputOnResetRef = useRef<string | null>(null);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const loggedInEmail = getUserAttributes()?.email?.trim().toLowerCase();
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [togglingDomains, setTogglingDomains] = useState<Set<string>>(new Set());
  const [localDomainAccessState, setLocalDomainAccessState] = useState<Record<string, boolean>>({});
  const [revokedUserIdentifiers, setRevokedUserIdentifiers] = useState<Set<string>>(new Set());
  const [awaitingSharedUsersRefetch, setAwaitingSharedUsersRefetch] = useState(false);
  const hasExternalGroupRef = useRef(false);

  const blurInput = () => {
    setFocused(false);
    inputRef.current?.blur();
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  const [isCopySuccess, setIsCopySuccess] = useState(false);
  const [isSendEmailSuccess, setIsSendEmailSuccess] = useState(false);
  const copySuccessTimerRef = useRef<NodeJS.Timeout | null>(null);
  const sendSuccessTimerRef = useRef<NodeJS.Timeout | null>(null);
  const showSendEmailButton = false;

  useEffect(() => {
    setSelectedContentTab(initialTab);
    setSelectedRecordingId(recordingTabs.length === 1 ? recordingTabs[0].groupId : "");
    setSelectedClipId(clipOptions.length === 1 ? clipOptions[0].id : "");
    setSelectMenuAnchor(null);
  }, [interactionId, open, recordingTabs, clipOptions, initialTab]);

  useEffect(() => {
    setShareUrl("");
    setTokenId("");
  }, [selectedContentTab, selectedClipId, selectedRecordingId]);

  useEffect(() => {
    return () => {
      if (copySuccessTimerRef.current) clearTimeout(copySuccessTimerRef.current);
      if (sendSuccessTimerRef.current) clearTimeout(sendSuccessTimerRef.current);
    };
  }, []);

  const selectedEmails = useMemo(
    () => new Set(emails.map((email) => email.trim().toLowerCase())),
    [emails]
  );

  // Fetch active users to suggest in Autocomplete
  const { data: activeUsersData, isLoading: isLoadingUsers } =
    UserInteractionsService.useGetActiveUsers(
      { interactionId },
      open && !!interactionId
    );

  // Fetch users with whom this interaction is currently shared
  const { data: sharedUsersData, refetch: refetchSharedUsers } =
    UserInteractionsService.useGetSharedUsers(
      { interactionId, 
        sharedAssetIds: selectedSharedAssetIds,
        assetCategory: isClipTab ? assetCategory.CLIP : assetCategory.FULL_RECORDING, 
      },
      canLoadSelectedAssetSharingData
    );

  const { mutate: shareInteractionViaEmail } =
    UserInteractionsService.useShareInteractionViaEmail();

  const {
    isSuccess: isSuccessSharedUrl,
    isLoading: isLoadingSharedUrl,
    isFetching: isFetchingSharedUrl,
    isError: isErrorSharedUrl,
    error: errorSharedUrl,
    data: sharedUrlData,
    refetch: refetchShareUrl,
  } = UserInteractionsService.useGetShareUrl(
    { 
      interactionId: interactionId || "",
      sharedAssetIds: selectedSharedAssetIds,
      assetCategory: isClipTab ? assetCategory.CLIP : assetCategory.FULL_RECORDING,
    },
    canLoadSelectedAssetSharingData
  );

  const refetchSelectedAssetSharingData = async () => {
    if (!canLoadSelectedAssetSharingData) return;
    await Promise.all([refetchSharedUsers(), refetchShareUrl()]);
  };

  useEffect(() => {
    setShowSpinner(isLoadingSharedUrl || isFetchingSharedUrl);
    if (isSuccessSharedUrl && sharedUrlData) {
      const data = sharedUrlData?.data;
      if (data?.url) setShareUrl(data.url);
      if (data?.tokenId) setTokenId(data.tokenId);
    }
  }, [isLoadingSharedUrl, isFetchingSharedUrl, isSuccessSharedUrl, sharedUrlData]);

  useEffect(() => {
    if (isErrorSharedUrl && open) {
      showSnackbar(
        "error",
        errorSharedUrl?.response?.data?.error || "Failed to fetch the shared url"
      );
    }
  }, [isErrorSharedUrl, open]);

  const sharedUsersList = useMemo(() => {
    return sharedUsersData?.data?.sharedUsers ?? [];
  }, [sharedUsersData]);

  const sharedEmailsSet = useMemo(() => {
    const set = new Set<string>();
    sharedUsersList.forEach((u: any) => {
      const email = u?.email?.trim().toLowerCase();
      const porId = u?.profileOrgRelationshipId;
      if (
        email &&
        u?.accessType?.toLowerCase() !== "external_domain_share" &&
        !revokedUserIdentifiers.has(email) &&
        (!porId || !revokedUserIdentifiers.has(porId))
      ) {
        set.add(email);
      }
    });
    return set;
  }, [sharedUsersList, revokedUserIdentifiers]);

  const sharedExternalDomainsSet = useMemo(() => {
    const set = new Set<string>();

    sharedUsersList.forEach((u: any) => {
      if (u?.accessType?.toLowerCase() !== "external_domain_share") return;
      const raw = (u.email || "").trim().toLowerCase();
      const domain = raw.includes("@") ? getFullEmailDomain(raw) : raw;
      if (domain && domain.includes(".") && !isExcludedDomain(domain)) {
        set.add(domain);
      }
    });

    return set;
  }, [sharedUsersList]);

  useEffect(() => {
    if (awaitingSharedUsersRefetch) return;
    setRevokedUserIdentifiers((prev) => (prev.size === 0 ? prev : new Set()));
  }, [sharedUsersData, awaitingSharedUsersRefetch]);

  useEffect(() => {
    setLocalDomainAccessState((prev) => {
      const entries = Object.entries(prev);
      if (entries.length === 0) return prev;

      let changed = false;
      const next = { ...prev };
      entries.forEach(([domain, enabled]) => {
        if (togglingDomains.has(domain) || awaitingSharedUsersRefetch) return;
        if (sharedExternalDomainsSet.has(domain) === enabled) {
          delete next[domain];
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  }, [sharedExternalDomainsSet, togglingDomains, awaitingSharedUsersRefetch]);

  const unmarkRevokedEmails = (emailsToUnmark: string[]) => {
    const emailSet = new Set(
      emailsToUnmark.map((email) => email?.trim().toLowerCase()).filter(Boolean)
    );
    if (emailSet.size === 0) return;

    setRevokedUserIdentifiers((prev) => {
      if (prev.size === 0) return prev;
      const next = new Set(prev);
      let changed = false;
      emailSet.forEach((email) => {
        if (next.delete(email)) changed = true;
      });
      sharedUsersList.forEach((user: any) => {
        const email = user?.email?.trim().toLowerCase();
        const porId = user?.profileOrgRelationshipId;
        if (email && emailSet.has(email) && porId && next.delete(porId)) {
          changed = true;
        }
      });
      return changed ? next : prev;
    });
  };

  const isDomainToggleBusy = togglingDomains.size > 0;
  const isRevokeLocked = !!revokingId || awaitingSharedUsersRefetch;
  const hasDomainWideAccess = useMemo(() => {
    if ([...sharedExternalDomainsSet].some((domain) => localDomainAccessState[domain] !== false)) {
      return true;
    }
    return Object.values(localDomainAccessState).some(Boolean);
  }, [sharedExternalDomainsSet, localDomainAccessState]);

  const handleToggleDomainAccess = (domain: string, shouldEnable: boolean) => {
    const normalizedDomain = domain?.trim().toLowerCase();
    if (
      !normalizedDomain ||
      isExcludedDomain(normalizedDomain) ||
      !interactionId ||
      isSending ||
      isDomainToggleBusy ||
      isRevokeLocked ||
      togglingDomains.has(normalizedDomain)
    )
      return;
    if (shouldEnable && hasUnselectedMultipleOptions) {
      showSnackbar("error", `Select a ${isClipTab ? "clip" : "recording"} to share first.`);
      return;
    }

    setTogglingDomains((prev) => new Set(prev).add(normalizedDomain));
    setLocalDomainAccessState((prev) => ({
      ...prev,
      [normalizedDomain]: shouldEnable,
    }));

    shareInteractionViaEmail(
      {
        interactionId,
        tokenId,
        ...(shouldEnable
          ? { sharedWithExternalDomain: normalizedDomain }
          : { revokeExternalDomain: normalizedDomain }),
        sharedAssetIds: getSelectedSharedAssetIds(),
      },
      {
        onSuccess: (data: any) => {
          setTogglingDomains((prev) => {
            const next = new Set(prev);
            next.delete(normalizedDomain);
            return next;
          });
          showSnackbar(
            "success",
            data?.data?.msg ||
              (shouldEnable
                ? `Domain access allowed for ${normalizedDomain}`
                : `Domain access revoked for ${normalizedDomain}`)
          );
          void refetchSelectedAssetSharingData();
        },
        onError: (err: any) => {
          setTogglingDomains((prev) => {
            const next = new Set(prev);
            next.delete(normalizedDomain);
            return next;
          });
          setLocalDomainAccessState((prev) => {
            const next = { ...prev };
            delete next[normalizedDomain];
            return next;
          });
          const response = err?.response?.data;
          const message =
            response?.msg || response?.error || "Failed to update domain access";
          showSnackbar("error", message);
        },
      }
    );
  };

  const pendingEmailsList = useMemo(() => {
    return emails.filter(
      (emailStr) => !sharedEmailsSet.has(emailStr.trim().toLowerCase())
    );
  }, [emails, sharedEmailsSet]);

  const options = useMemo(() => {
    const rawList = activeUsersData?.data?.data ?? [];
    const list = Array.isArray(rawList) ? rawList : [];
    const seenEmails = new Set<string>();

    return list
      .filter((user: any) => {
        if (!user?.email) return false;
        const email = user.email.trim().toLowerCase();

        // Remove duplicates
        if (seenEmails.has(email)) {
          return false;
        }
        seenEmails.add(email);
        return true;
      })
      .map((user: any) => ({
        name: user.name || "",
        email: user.email?.trim()?.toLowerCase(),
        isParticipant: user.isParticipant,
        accountId: user.accountId,
      }));
  }, [activeUsersData]);

  const isInternalEmail = (email: string, user?: any) => {
    const emailDomain = getFullEmailDomain(email);
    const loggedInDomain = getFullEmailDomain(loggedInEmail);
    const emailLower = email?.trim().toLowerCase();
    const matchedOpt = options.find((opt: any) => opt.email === emailLower);

    if (loggedInDomain && emailDomain && emailDomain === loggedInDomain) {
      return true;
    }

    if (
      loggedInAccountId &&
      (user?.accountId === loggedInAccountId ||
        matchedOpt?.accountId === loggedInAccountId)
    ) {
      return true;
    }

    return false;
  };

  const isExternalEmail = (email: string, user?: any) =>
    !!email && !isInternalEmail(email, user);

  const isRevokedIdentifier = (porId?: string, email?: string) => {
    if (porId && revokedUserIdentifiers.has(porId)) return true;
    const emailLower = email?.trim().toLowerCase();
    return !!emailLower && revokedUserIdentifiers.has(emailLower);
  };

  const isDomainAccessEnabled = (domain?: string) => {
    if (!domain) return false;
    if (localDomainAccessState[domain] === true) return true;
    if (localDomainAccessState[domain] === false) return false;
    return sharedExternalDomainsSet.has(domain);
  };

  const hasRemainingPeopleInDomain = (
    domain: string,
    exclude: { porId?: string; email?: string }
  ) => {
    const excludeEmail = exclude.email?.trim().toLowerCase();

    const hasOtherShared = sharedUsersList.some((sharedUser: any) => {
      if (sharedUser?.accessType?.toLowerCase() === "external_domain_share") {
        return false;
      }
      const sharedEmail = (sharedUser.email || "").trim();
      const sharedPorId = sharedUser.profileOrgRelationshipId;
      if (exclude.porId && sharedPorId === exclude.porId) return false;
      if (excludeEmail && sharedEmail.toLowerCase() === excludeEmail) return false;
      if (isRevokedIdentifier(sharedPorId, sharedEmail)) return false;
      return (
        getFullEmailDomain(sharedEmail) === domain &&
        isExternalEmail(sharedEmail, sharedUser)
      );
    });
    if (hasOtherShared) return true;

    return pendingEmailsList.some((pendingEmail) => {
      const pending = pendingEmail.trim();
      const pendingLower = pending.toLowerCase();
      if (excludeEmail && pendingLower === excludeEmail) return false;
      if (isRevokedIdentifier(undefined, pending)) return false;
      const matchedOpt = options.find((opt: any) => opt.email === pendingLower);
      return (
        getFullEmailDomain(pending) === domain &&
        isExternalEmail(pending, matchedOpt)
      );
    });
  };

  const { internalPeople, externalDomainGroups } = useMemo(() => {
    const people: AccessPerson[] = [];
    const loggedInDomain = getFullEmailDomain(loggedInEmail);

    pendingEmailsList.forEach((emailStr: string, index: number) => {
      const email = emailStr.trim();
      const emailLower = email.toLowerCase();
      if (revokedUserIdentifiers.has(emailLower)) return;
      const matchedOpt = options.find((opt: any) => opt.email === emailLower);
      const domain = getFullEmailDomain(email);

      people.push({
        key: `pending-${emailLower}-${index}`,
        kind: "pending",
        email,
        name: matchedOpt?.name || "",
        isExternal: isExternalEmail(email, matchedOpt),
        domain,
      });
    });

    sharedUsersList.forEach((user: any, index: number) => {
      if (user?.accessType?.toLowerCase() === "external_domain_share") {
        return;
      }
      const email = (user.email || "").trim();
      const porId = user.profileOrgRelationshipId;
      const emailLower = email.toLowerCase();

      if (
        revokedUserIdentifiers.has(emailLower) ||
        (porId && revokedUserIdentifiers.has(porId))
      ) {
        return;
      }

      const domain = getFullEmailDomain(email);

      people.push({
        key: `shared-${porId || email || index}`,
        kind: "shared",
        email,
        name: user.name || "",
        pictureUrl: user.pictureUrl || "",
        user,
        isExternal: isExternalEmail(email, user),
        domain,
      });
    });

    const internals = people.filter((person) => !person.isExternal);
    const domainMap = new Map<string, AccessPerson[]>();

    people
      .filter(
        (person) =>
          person.isExternal &&
          person.domain &&
          person.domain !== loggedInDomain
      )
      .forEach((person) => {
        const group = domainMap.get(person.domain) || [];
        group.push(person);
        domainMap.set(person.domain, group);
      });

    const activeDomainsSet = new Set<string>(sharedExternalDomainsSet);
    Object.entries(localDomainAccessState).forEach(([domain, enabled]) => {
      if (enabled) {
        activeDomainsSet.add(domain);
      } else if (enabled === false) {
        activeDomainsSet.delete(domain);
      }
    });

    activeDomainsSet.forEach((domain) => {
      if (
        domain &&
        domain !== loggedInDomain &&
        !isExcludedDomain(domain) &&
        !domainMap.has(domain)
      ) {
        domainMap.set(domain, []);
      }
    });

    return {
      internalPeople: internals,
      externalDomainGroups: Array.from(domainMap.entries()).map(
        ([domain, groupPeople]) => ({ domain, people: groupPeople })
      ),
    };
  }, [
    pendingEmailsList,
    sharedUsersList,
    options,
    loggedInEmail,
    loggedInAccountId,
    sharedExternalDomainsSet,
    localDomainAccessState,
    revokedUserIdentifiers,
  ]);

  // Helper to validate email inputs/list and categorize internal vs external
  const validateAndGetEmails = (overrideInput?: string) => {
    let finalEmails = [...emails];
    const currentInput = (overrideInput !== undefined ? overrideInput : inputValue).trim();

    if (currentInput) {
      const currentInputLower = currentInput.toLowerCase();

      if (currentInputLower === loggedInEmail) {
        keepInputOnResetRef.current = currentInput;
        setEmailError("You can't share an interaction with yourself.");
        setInputValue(currentInput);
        return {
          isValid: false,
          errorMessage: "You can't share an interaction with yourself.",
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }

      if (!validateEmail(currentInput)) {
        keepInputOnResetRef.current = currentInput;
        setEmailError("Enter valid email");
        setInputValue(currentInput);
        return {
          isValid: false,
          errorMessage: "Enter valid email",
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }

      if (sharedEmailsSet.has(currentInputLower)) {
        keepInputOnResetRef.current = currentInput;
        setEmailError("This user already has access.");
        setInputValue(currentInput);
        return {
          isValid: false,
          errorMessage: "This user already has access.",
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }

      if (
        !finalEmails.some(
          (email) => email.trim().toLowerCase() === currentInputLower
        )
      ) {
        finalEmails.push(currentInput);
      }
    }

    if (finalEmails.length > 0) {
      const invalidEmailsList = finalEmails.filter(
        (email) => !validateEmail(email)
      );
      if (invalidEmailsList.length > 0) {
        return {
          isValid: false,
          errorMessage: `Invalid email address(es) detected: ${invalidEmailsList.join(", ")}`,
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }

      const hasSelfShare = finalEmails.some(
        (email) => email.trim().toLowerCase() === loggedInEmail
      );
      if (hasSelfShare) {
        return {
          isValid: false,
          errorMessage: "You can't share an interaction with yourself.",
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }

      const alreadyShared = finalEmails.filter((email) =>
        sharedEmailsSet.has(email.trim().toLowerCase())
      );
      if (alreadyShared.length > 0) {
        setEmailError("This user already has access.");
        return {
          isValid: false,
          errorMessage: `The following user(s) already have access: ${alreadyShared.join(", ")}`,
          finalEmails: [],
          sharedWithInternalEmails: [],
          sharedWithExternalEmails: [],
        };
      }
    }

    const loggedInDomain = getFullEmailDomain(loggedInEmail)?.toLocaleLowerCase();

    const sharedWithInternalEmails: string[] = [];
    const sharedWithExternalEmails: string[] = [];

    finalEmails.forEach((email) => {
      const emailTrimmed = email?.trim();
      const emailLower = emailTrimmed?.toLowerCase();
      const emailDomain = getFullEmailDomain(emailTrimmed)?.toLocaleLowerCase();

      const matchedOpt = options.find(
        (opt: any) => opt.email === emailLower
      );

      const isInternalUser =
        (loggedInDomain && emailDomain === loggedInDomain) ||
        (loggedInAccountId && matchedOpt?.accountId === loggedInAccountId);

      if (isInternalUser) {
        sharedWithInternalEmails.push(emailTrimmed);
      } else {
        sharedWithExternalEmails.push(emailTrimmed);
      }
    });

    return {
      isValid: true,
      finalEmails,
      sharedWithInternalEmails,
      sharedWithExternalEmails,
    };
  };

  const handleCopyLink = async () => {
    if (
      !interactionId ||
      isSending ||
      isDomainToggleBusy ||
      isRevokeLocked ||
      isLoadingSharedUrl ||
      isFetchingSharedUrl
    )
      return;

    const existingUrl = shareUrl || sharedUrlData?.data?.url;
    if (!existingUrl) {
      showSnackbar("error", "Share URL is not available");
      return;
    }

    try {
      await navigator.clipboard.writeText(existingUrl);
      setIsCopySuccess(true);
      if (copySuccessTimerRef.current) clearTimeout(copySuccessTimerRef.current);
      copySuccessTimerRef.current = setTimeout(() => {
        setIsCopySuccess(false);
      }, 5000);
    } catch (err) {
      showSnackbar("error", "Failed to copy link to clipboard");
    }
  };

  const handleShareViaEmailOnInput = (overrideInput?: string) => {
    if (
      !interactionId ||
      isSending ||
      isDomainToggleBusy ||
      isRevokeLocked ||
      isLoadingSharedUrl ||
      isFetchingSharedUrl
    )
      return;
    if (hasUnselectedMultipleOptions) {
      showSnackbar("error", `Select a ${isClipTab ? "clip" : "recording"} to share first.`);
      return;
    }

    const result = validateAndGetEmails(overrideInput);
    if (!result.isValid) {
      if (result.errorMessage) {
        showSnackbar("error", result.errorMessage);
      }
      return;
    }

    if (result.finalEmails.length === 0) {
      return;
    }

    setIsSending(true);
    setShowSpinner(true);
    blurInput();

    const hasInternalShare = result.sharedWithInternalEmails.length > 0;
    const shareType = hasInternalShare && !isClipTab ? "SEND_EMAIL" : "COPY_LINK";

    shareInteractionViaEmail(
      {
        interactionId,
        sharedWithInternalEmails: result.sharedWithInternalEmails,
        sharedWithExternalEmails: result.sharedWithExternalEmails,
        tokenId,
        shareType,
        sharedAssetIds: getSelectedSharedAssetIds(),
        assetCategory: isClipTab ?  assetCategory.CLIP : assetCategory.FULL_RECORDING,
      },
      {
        onSuccess: async (res: any) => {
          const data = res?.data;
          const generatedUrl = data?.url || data?.shareLink;

          if (generatedUrl) {
            setShareUrl(generatedUrl);
          }

          keepInputOnResetRef.current = null;
          setEmails([]);
          setInputValue("");
          setEmailError("");
          unmarkRevokedEmails([
            ...result.sharedWithInternalEmails,
            ...result.sharedWithExternalEmails,
          ]);
          void refetchSelectedAssetSharingData();
          setIsSending(false);
          setShowSpinner(false);
          blurInput();
        },
        onError: (err: any) => {
          showSnackbar(
            "error",
            err?.response?.data?.error || "Failed to share interaction"
          );
          setIsSending(false);
          setShowSpinner(false);
        },
      }
    );
  };

  const handleRevokeUser = (user: any) => {
    const userPorId = user.profileOrgRelationshipId;
    const email = user.email?.trim();
    const emailLower = email?.toLowerCase();
    const isExternalUser =
      user?.accessType?.toLowerCase() === "external_share" ||
      (!userPorId && email);

    const identifier = userPorId || email;
    if (!identifier || isDomainToggleBusy || isSending || isRevokeLocked) return;

    const domain = getFullEmailDomain(email);
    const loggedInDomain = getFullEmailDomain(loggedInEmail);
    const shouldRevokeDomain =
      isDomainAccessEnabled(domain) &&
      isExternalEmail(email, user) &&
      !!domain &&
      domain !== loggedInDomain &&
      !isExcludedDomain(domain) &&
      !hasRemainingPeopleInDomain(domain, { porId: userPorId, email });

    setRevokingId(identifier);
    setAwaitingSharedUsersRefetch(true);
    setRevokedUserIdentifiers((prev) => {
      const next = new Set(prev);
      if (userPorId) next.add(userPorId);
      if (emailLower) next.add(emailLower);
      return next;
    });

    if (shouldRevokeDomain) {
      setLocalDomainAccessState((prev) => ({
        ...prev,
        [domain]: false,
      }));
    }

    const payload: {
      interactionId: string;
      tokenId: string;
      sharedAssetIds: string[];
      revokeInternalProfileOrgRelationshipIds?: string[];
      revokeExternalEmails?: string[];
      revokeExternalDomain?: string;
      assetCategory?: assetCategory;
    } =
      !isExternalUser && userPorId && !isClipTab
        ? {
            interactionId,
            revokeInternalProfileOrgRelationshipIds: [userPorId],
            revokeExternalEmails: [email],
            tokenId,
            sharedAssetIds: getSelectedSharedAssetIds(),
            assetCategory: isClipTab ?  assetCategory.CLIP : assetCategory.FULL_RECORDING,
          }
        : {
            interactionId,
            revokeExternalEmails: [email],
            tokenId,
            sharedAssetIds: getSelectedSharedAssetIds(),
            assetCategory: isClipTab ?  assetCategory.CLIP : assetCategory.FULL_RECORDING,
          };

    if (shouldRevokeDomain) {
      payload.revokeExternalDomain = domain;
    }

    shareInteractionViaEmail(payload, {
      onSuccess: async (data) => {
        const resData = data?.data;
        if (resData?.shareLink) {
          setShareUrl(resData.shareLink);
        }
        showSnackbar(
          "success",
          data?.data?.msg ||
            (shouldRevokeDomain
              ? `Access and domain access for ${domain} revoked successfully`
              : "Access revoked successfully")
        );
        try {
          await refetchSelectedAssetSharingData();
        } finally {
          if (domain) {
            setLocalDomainAccessState((prev) => {
              if (prev[domain] === undefined) return prev;
              const next = { ...prev };
              delete next[domain];
              return next;
            });
          }
          setRevokingId(null);
          setAwaitingSharedUsersRefetch(false);
          setRevokedUserIdentifiers(new Set());
        }
      },
      onError: (err: any) => {
        setRevokingId(null);
        setAwaitingSharedUsersRefetch(false);
        setRevokedUserIdentifiers((prev) => {
          const next = new Set(prev);
          if (userPorId) next.delete(userPorId);
          if (emailLower) next.delete(emailLower);
          return next;
        });
        if (shouldRevokeDomain) {
          setLocalDomainAccessState((prev) => {
            const next = { ...prev };
            delete next[domain];
            return next;
          });
        }
        const response = err?.response?.data;
        const message =
          response?.msg || response?.error || "Failed to revoke access";
        showSnackbar("error", message);
      },
    });
  };

  const handleRemovePendingEmail = (emailToRemove: string) => {
    const email = emailToRemove.trim();
    const domain = getFullEmailDomain(email);
    const loggedInDomain = getFullEmailDomain(loggedInEmail);

    setEmails((prev) =>
      prev.filter((e) => e.trim().toLowerCase() !== email.toLowerCase())
    );

    if (
      isDomainAccessEnabled(domain) &&
      !!domain &&
      domain !== loggedInDomain &&
      !isExcludedDomain(domain) &&
      !isRevokeLocked &&
      !isSending &&
      !isDomainToggleBusy &&
      !hasRemainingPeopleInDomain(domain, { email })
    ) {
      handleToggleDomainAccess(domain, false);
    }
  };

  // Map simple email strings state to UserOption objects for the Autocomplete component
  const selectedOptions = useMemo(() => {
    return emails.map((email) => {
      const match = options.find(
        (opt: any) => opt.email === email.toLowerCase()
      );
      return match || { name: email, email, isParticipant: false, accountId: undefined };
    });
  }, [emails, options]);

  const handleSendEmail = () => {
    if (
      isLoadingSharedUrl ||
      isFetchingSharedUrl ||
      isDomainToggleBusy ||
      isRevokeLocked
    )
      return;
    if (hasUnselectedMultipleOptions) {
      showSnackbar("error", `Select a ${isClipTab ? "clip" : "recording"} to share first.`);
      return;
    }

    const result = validateAndGetEmails();
    if (!result.isValid) {
      if (result.errorMessage) {
        showSnackbar("error", result.errorMessage);
      }
      return;
    }

    if (result.finalEmails.length === 0) {
      showSnackbar("error", "Please add at least one recipient email.");
      return;
    }

    setIsSending(true);

    shareInteractionViaEmail(
      {
        interactionId,
        sharedWithInternalEmails: result.sharedWithInternalEmails,
        sharedWithExternalEmails: result.sharedWithExternalEmails,
        tokenId,
        shareType: "SEND_EMAIL",
        sharedAssetIds: getSelectedSharedAssetIds(),
        assetCategory: isClipTab ?  assetCategory.CLIP : assetCategory.FULL_RECORDING,
      },
      {
        onSuccess: () => {
          setIsSending(false);
          setEmails([]);
          setInputValue("");
          setEmailError("");
          unmarkRevokedEmails([
            ...result.sharedWithInternalEmails,
            ...result.sharedWithExternalEmails,
          ]);
          void refetchSelectedAssetSharingData();
          setIsSendEmailSuccess(true);
          if (sendSuccessTimerRef.current) clearTimeout(sendSuccessTimerRef.current);
          sendSuccessTimerRef.current = setTimeout(() => {
            setIsSendEmailSuccess(false);
          }, 5000);
        },
        onError: (err: any) => {
          setIsSending(false);
          const response = err?.response?.data;
          const message = response?.msg || "Failed to share interaction";
          showSnackbar("error", message);
        },
      }
    );
  };

  const handleClose = () => {
    setEmails([]);
    setInputValue("");
    setEmailError("");
    setShareUrl("");
    setTokenId("");
    keepInputOnResetRef.current = null;
    setRevokingId(null);
    setAwaitingSharedUsersRefetch(false);
    setTogglingDomains(new Set());
    setLocalDomainAccessState({});
    setRevokedUserIdentifiers(new Set());
    setIsSending(false);
    setFocused(false);
    setShowSpinner(false);
    setIsCopySuccess(false);
    setIsSendEmailSuccess(false);
    if (copySuccessTimerRef.current) clearTimeout(copySuccessTimerRef.current);
    if (sendSuccessTimerRef.current) clearTimeout(sendSuccessTimerRef.current);
    onClose();
  };

  const hasRecipients = emails.length > 0;
  const hasActiveSharedUsers = useMemo(() => {
    return sharedUsersList.some((u: any) => {
      if (u?.accessType?.toLowerCase() === "external_domain_share") return false;
      const email = u?.email?.trim()?.toLowerCase();
      const porId = u?.profileOrgRelationshipId;
      if (email && revokedUserIdentifiers.has(email)) return false;
      if (porId && revokedUserIdentifiers.has(porId)) return false;
      return true;
    });
  }, [sharedUsersList, revokedUserIdentifiers]);

  const canCopyLink =
    (hasActiveSharedUsers || hasDomainWideAccess) &&
    !!(shareUrl || sharedUrlData?.data?.url);
  const isUrlLoading = isLoadingSharedUrl || isFetchingSharedUrl;
  const hasAccessList = internalPeople.length > 0 || externalDomainGroups.length > 0;

  const renderAccessPerson = (person: AccessPerson) => {
    const avatarInitials = getAvatarInitials(person.name || person.email, person.email);
    const avatarColor = getAvatarColor(person.name || person.email, person.email);
    const porId = person.user?.profileOrgRelationshipId;
    const isThisRevoking = revokingId === (porId || person.email);

    return (
      <Box
        key={person.key}
        className="share-interaction-user-item"
      >
        <Box className="share-interaction-user-row">
          <Box className="share-interaction-user-left">
            <Avatar
              src={person.pictureUrl || ""}
              className="search-box-option-avatar"
              sx={{ backgroundColor: avatarColor }}
            >
              {!person.pictureUrl && avatarInitials}
            </Avatar>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                minWidth: 0,
              }}
            >
              {person.name ? (
                <Typography className="search-box-option-name">
                  {person.name}
                  {person.email && (
                    <Typography
                      component="span"
                      className="search-box-option-email"
                    >
                      {" "}({person.email})
                    </Typography>
                  )}
                </Typography>
              ) : (
                <Typography className="search-box-option-email">
                  {person.email}
                </Typography>
              )}
            </Box>
          </Box>
          <Tooltip
            title={person.kind === "pending" ? "Remove email" : "Revoke access"}
            arrow
            placement="top"
          >
            <span>
              <IconButton
                size="small"
                className="share-interaction-revoke-btn"
                onClick={() =>
                  person.kind === "pending"
                    ? handleRemovePendingEmail(person.email)
                    : handleRevokeUser(person.user)
                }
                disabled={person.kind === "shared" && (isRevokeLocked || isSending || isDomainToggleBusy)}
              >
                {isThisRevoking ? (
                  <CircularProgress size={14} color="inherit" />
                ) : (
                  <CloseIcon sx={{ fontSize: 16 }} />
                )}
              </IconButton>
            </span>
          </Tooltip>
        </Box>
      </Box>
    );
  };

  const renderDomainCheckbox = (domain: string) => {
    if (!domain) return null;
    const loggedInDomain = getFullEmailDomain(loggedInEmail);
    if (domain === loggedInDomain) return null;
    if (isExcludedDomain(domain)) return null;

    const isDomainAccessChecked =
      localDomainAccessState[domain] !== undefined
        ? localDomainAccessState[domain]
        : sharedExternalDomainsSet.has(domain);
    const isDomainToggling = togglingDomains.has(domain);
    const isToggleDisabled =
      isDomainToggling ||
      isSending ||
      isDomainToggleBusy ||
      isRevokeLocked;

    return (
      <Box className="share-interaction-domain-header">
        <Typography className="share-interaction-domain-name">
          {domain}
        </Typography>
        <Box className="share-interaction-domain-access">
        <Checkbox
          size="small"
          checked={isDomainAccessChecked}
          disabled={isToggleDisabled}
          onChange={(e) => handleToggleDomainAccess(domain, e.target.checked)}
          className="share-interaction-domain-checkbox"
          sx={{
            padding: "2px",
            color: "#6F6F7F",
            transform: "scale(0.75)",
            "&.Mui-checked": {
              color: "#7F56D9",
            },
            "&.Mui-checked:hover": {
              color: "#6941C6",
            },
            "&:hover:not(.Mui-checked) .MuiSvgIcon-root": {
              color: "#7F56D9",
            },
          }}
        />
        <Typography className="share-interaction-domain-checkbox-text">
          Allow anyone from this domain to access
        </Typography>
        </Box>
      </Box>
    );
  };

  return (
    <>
    <Dialog
      open={open}
      onClose={handleClose}
      PaperProps={{
        className: "share-interaction-container"
      }}
    >
      <Box className="share-interaction-header">
        <Box className="share-interaction-header-top">
          <Typography className="share-interaction-header-title">
            Share
          </Typography>
          <Box className="close-btn-container">
            <IconButton
              onClick={handleClose}
              className="close-btn-icon"
            >
              <CloseIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Box>
        </Box>
      </Box>

      <Box className="share-interaction-body">
        <Box className="share-interaction-content-tabs" role="tablist" aria-label="Content to share">
          <button
            type="button"
            role="tab"
            aria-selected={selectedContentTab === "recording"}
            className={`share-interaction-content-tab ${selectedContentTab === "recording" ? "selected" : ""}`}
            onClick={() => {
              setShareUrl("");
              setTokenId("");
              setSelectedContentTab("recording");
            }}
          >
            <img
              src={InteractionIcon}
              alt=""
              className={selectedContentTab === "recording" ? "selected" : ""}
            />
            Full Recording
          </button>
          {hasClips && (
            <button
              type="button"
              role="tab"
              aria-selected={selectedContentTab === "clips"}
              className={`share-interaction-content-tab ${selectedContentTab === "clips" ? "selected" : ""}`}
              onClick={() => {
                setShareUrl("");
                setTokenId("");
                setSelectedContentTab("clips");
              }}
            >
              <img
                src={ClipIcon}
                alt=""
                className={selectedContentTab === "clips" ? "selected" : ""}
              />
              Clips
            </button>
          )}
        </Box>
        {showSelectOptions && (
          <Box className="share-interaction-select-picker">
            <button
              type="button"
              className={`share-interaction-select-picker-trigger ${!selectedOption ? "share-interaction-select-option-placeholder" : ""}`}
              aria-haspopup="listbox"
              aria-expanded={Boolean(selectMenuAnchor)}
              onClick={(event) => setSelectMenuAnchor(event.currentTarget)}
            >
              {selectedOption ? (
                <Box className="share-interaction-selected-option">
                  <img
                    className="share-interaction-single-option-thumbnail"
                    src={sharedVideoIcon}
                    alt=""
                  />
                  <Box className="share-interaction-clip-option-content">
                    <Box className="share-interaction-clip-option-heading">
                      <Typography className="share-interaction-clip-option-name">
                        {selectedOption.label}
                      </Typography>
                      {selectedOption.detail && (
                        <>
                          <span className="share-interaction-clip-option-divider" aria-hidden="true" />
                          <Typography className="share-interaction-clip-option-duration">
                            {selectedOption.detail}
                          </Typography>
                        </>
                      )}
                    </Box>
                    {isClipTab && selectedOption.createdBy && (
                      <Typography className="share-interaction-clip-option-creator">
                        Created by: {selectedOption.createdBy}
                      </Typography>
                    )}
                  </Box>
                </Box>
              ) : (
                <span className="share-interaction-recording-option-label">
                  {isClipTab ? "Select Clip" : "Select Recording"}
                </span>
              )}
              <img src={expandIcon} alt="" className="expand-icon"/>
            </button>
            <Menu
              anchorEl={selectMenuAnchor}
              open={Boolean(selectMenuAnchor)}
              onClose={() => setSelectMenuAnchor(null)}
              MenuListProps={{
                role: "listbox",
                "aria-label": isClipTab ? "Available clips" : "Available recordings",
              }}
              PaperProps={{ className: "share-interaction-select-menu" }}
              anchorOrigin={{ vertical: "bottom", horizontal: "left" }}
              transformOrigin={{ vertical: "top", horizontal: "left" }}
            >
              {selectOptions.map((option) => (
                <MenuItem
                  key={option.id}
                  role="option"
                  aria-selected={selectedOptionId === option.id}
                  className={`share-interaction-clip-option ${selectedOptionId === option.id ? "selected" : ""}`}
                  onClick={() => {
                    if (isClipTab) setSelectedClipId(option.id);
                    else setSelectedRecordingId(option.id);
                    setSelectMenuAnchor(null);
                    setShareUrl("");
                    setTokenId("");
                  }}
                >
                  <Box className="share-interaction-select-option-row">
                    <img
                      className="share-interaction-single-option-thumbnail"
                      src={sharedVideoIcon}
                      alt=""
                    />
                    <Box className="share-interaction-clip-option-content">
                      <Box className="share-interaction-clip-option-heading">
                        <Typography className="share-interaction-clip-option-name">
                          {option.label}
                        </Typography>
                        {option.detail && (
                          <>
                            <span className="share-interaction-clip-option-divider" aria-hidden="true" />
                            <Typography className="share-interaction-clip-option-duration">
                              {option.detail}
                            </Typography>
                          </>
                        )}
                      </Box>
                      {isClipTab && (
                        <Typography className="share-interaction-clip-option-creator">
                          Created by: {option.createdBy}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        )}
        {selectedOption && (
        <Box className="search-box-outer-container">
          <Autocomplete
            multiple
            freeSolo
            disableClearable
            autoHighlight
            options={options}
            value={selectedOptions}
            inputValue={inputValue}
            open={focused}
            getOptionLabel={(option) =>
              typeof option === "string" ? option : option.email || ""
            }
            onFocus={() => {
              setFocused(true);
            }}
            onBlur={() => {
              setFocused(false);
            }}
            onChange={(_, newValue) => {
              const selectedList = newValue as (string | UserOption)[];
              if (selectedList.length === 0) {
                setEmails([]);
                setInputValue("");
                setEmailError("");
                return;
              }

              const lastItem = selectedList[selectedList.length - 1];
              const emailStr =
                typeof lastItem === "string" ? lastItem.trim() : lastItem.email.trim();

              if (emailStr) {
                handleShareViaEmailOnInput(emailStr);
                blurInput();
              }
            }}
            onInputChange={(_, value, reason) => {
              if (reason === "reset") {
                if (keepInputOnResetRef.current !== null) {
                  const savedVal = keepInputOnResetRef.current;
                  keepInputOnResetRef.current = null;
                  setInputValue(savedVal);
                  return;
                }
                if (emailError) {
                  return;
                }
              }

              setInputValue(value);

              if (!value.trim()) {
                setEmailError("");
              } else if (validateEmail(value.trim())) {
                setEmailError("");
              }
            }}
            filterOptions={(options, state) => {
              const search = state.inputValue.trim().toLowerCase();

              const filtered = options.filter((opt: any) => {
                const email = opt.email;
                if (
                  email === loggedInEmail ||
                  sharedEmailsSet.has(email) ||
                  selectedEmails.has(email)
                ) {
                  return false;
                }

                if (!search) {
                  return !!opt.isParticipant;
                }

                return (
                  (opt.name?.toLowerCase() ?? "").startsWith(search) ||
                  email.startsWith(search)
                );
              });

              // Sort results: external users first, internal users second (based on domain or accountId)
              const loggedInDomain = getFullEmailDomain(loggedInEmail)?.toLocaleLowerCase();

              filtered.sort((a: any, b: any) => {
                const aDomain = getFullEmailDomain(a.email)?.toLocaleLowerCase();
                const bDomain = getFullEmailDomain(b.email)?.toLocaleLowerCase();

                const aIsInternal =
                  (!!loggedInDomain && aDomain === loggedInDomain) ||
                  (!!loggedInAccountId && a.accountId === loggedInAccountId);

                const bIsInternal =
                  (!!loggedInDomain && bDomain === loggedInDomain) ||
                  (!!loggedInAccountId && b.accountId === loggedInAccountId);

                if (!aIsInternal && bIsInternal) return -1;
                if (aIsInternal && !bIsInternal) return 1;
                return 0;
              });

              hasExternalGroupRef.current = filtered.some((opt: any) => {
                const domain = getFullEmailDomain(opt.email)?.toLocaleLowerCase();
                const isInternal =
                  (!!loggedInDomain && domain === loggedInDomain) ||
                  (!!loggedInAccountId && opt.accountId === loggedInAccountId);
                return !isInternal;
              });

              return filtered;
            }}
            groupBy={(option) => {
              const email = typeof option === "string" ? option : option.email;
              const domain = getFullEmailDomain(email)?.toLocaleLowerCase();
              const loggedInDomain = getFullEmailDomain(loggedInEmail)?.toLocaleLowerCase();

              const isInternal =
                (!!loggedInDomain && domain === loggedInDomain) ||
                (!!loggedInAccountId && typeof option !== "string" && option.accountId === loggedInAccountId);

              return isInternal ? "INTERNAL" : "EXTERNAL";
            }}
            renderGroup={(params) => (
              <li key={params.key}>
                {params.group === "INTERNAL" && hasExternalGroupRef.current && (
                  <Divider sx={{ borderColor: "#E5E7EB", my: 0.5 }} />
                )}
                <Box className="search-box-option-group-header">
                  {params.group}
                </Box>
                <ul style={{ padding: 0, margin: 0 }}>{params.children}</ul>
              </li>
            )}
            loading={isLoadingUsers}
            renderTags={() => null}
            renderOption={(props, option) => {
              const { key, ...optionProps } = props;

              const initials = getAvatarInitials(option.name, option.email);
              const color = getAvatarColor(option.name, option.email);
              return (
                <li
                  key={key}
                  {...optionProps}
                  className={`search-box-option-item ${optionProps.className || ""}`}
                >
                  <Avatar
                    className="search-box-option-avatar"
                    sx={{ backgroundColor: color }}
                  >
                    {initials}
                  </Avatar>
                  <Box
                    sx={{ display: "flex", alignItems: "center", gap: "6px" }}
                  >
                    <Typography className="search-box-option-name">
                      {option.name}
                    </Typography>
                    <Typography className="search-box-option-email">
                      ({option.email})
                    </Typography>
                  </Box>

                  <Typography
                    className="search-box-option-enter"
                    sx={{
                      fontSize: "16px",
                      color: "#777",
                      marginLeft: "auto",
                      lineHeight: 1,
                    }}
                  >
                    ↵
                  </Typography>
                </li>
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                inputRef={inputRef}
                placeholder="Add or Enter all email Id's to access recording."
                variant="outlined"
                fullWidth
                name="share-textfield"
                id="share-textfield"
                autoComplete="off"
                inputProps={{
                  ...params.inputProps,
                  autoComplete: "chrome-off",
                  spellCheck: false,
                  onKeyDown: (
                    e: React.KeyboardEvent<HTMLInputElement>
                  ) => {
                    if (
                      e.key === "Enter" ||
                      e.key === "ArrowDown" ||
                      e.key === "ArrowUp"
                    ) {
                      params.inputProps.onKeyDown?.(e);
                      return;
                    }

                    if (e.key !== "Enter" && e.key !== " ") {
                      return;
                    }
                    e.preventDefault();
                    e.stopPropagation();

                    const email = inputValue.trim();
                    if (!email) return;

                    handleShareViaEmailOnInput(email);
                    blurInput();
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  className: "search-box",
                  sx: {
                    "& input": {
                      color: emailError ? "#E84646" : "inherit",
                    },
                    "& input::placeholder": {
                      color: "#9CA3AF",
                      opacity: 1,
                    },
                  },
                }}
              />
            )}
          />
        </Box>
        )}

        {emailError && (
          <Typography className="share-interaction-error">
            {emailError}
          </Typography>
        )}
        {hasAccessList && (
          <Typography className="share-interaction-hint">
            People with Access to this {selectedContentTab === "recording" ? "Interaction" : " Clips"}:
          </Typography>
        )}

        {hasAccessList && (
          <Box className="share-interaction-users-list">
            {internalPeople.map((person) => renderAccessPerson(person))}

            {externalDomainGroups.map(({ domain, people }) => {
              const isExcluded = isExcludedDomain(domain);
              return (
                <Box key={domain} className="share-interaction-domain-group">
                  {renderDomainCheckbox(domain)}
                  {people.length > 0 && (
                    <Box className={!isExcluded ? "share-interaction-domain-users" : ""}>
                      {people.map((person) => renderAccessPerson(person))}
                    </Box>
                  )}
                </Box>
              );
            })}
          </Box>
        )}

        <Box sx={{ paddingTop: "20px" }}>
        <Box className="share-interaction-copy-link-container">
          <Box className="share-interaction-get-link-icon">
            <img src={RestrictIcon} alt="" className="share-interaction-icons"/>
            <Typography className="share-interaction-copy-link-container-text">
              <span className="restricted-text">Restricted</span> - Only users added to this list can access this content.
            </Typography>
          </Box>
          <Box className="share-interaction-get-link-icon">
            <img src={ExpiryIcon} alt="" className="share-interaction-icons"/>
            <Typography className="share-interaction-copy-link-container-text">
              Access Expires in 30 days
            </Typography>
          </Box>
          <Box className="share-interaction-get-link-icon">
            <img src={callRecordingIcon} alt="" className="share-interaction-icons"/>
            <Typography className="share-interaction-copy-link-container-text">
              {selectedContentTab === "recording" ? "Includes Call Recordings & Transcripts" : "Includes Clips & Transcripts"}
            </Typography>
          </Box>
        </Box>
        </Box>
        </Box>

      <Box className="share-interaction-btn-container">
        {showSendEmailButton && (
        <button
          className={`share-interaction-action-btn ${
            isSendEmailSuccess
              ? "success-state"
              : !hasRecipients || isSending || isUrlLoading || isDomainToggleBusy
              ? "disabled-look"
              : "outlined-state"
          }`}
          onClick={handleSendEmail}
          disabled={
            isSendEmailSuccess ||
            !hasRecipients ||
            isSending ||
            isDomainToggleBusy ||
            isRevokeLocked ||
            isUrlLoading
          }
        >
          {isSending ? (
            <CircularProgress size={16} color="inherit" />
          ) : isSendEmailSuccess ? (
            "Email Sent Successfully"
          ) : (
            "Send Email"
          )}
        </button>
        )}

        <button
          className={`share-interaction-action-btn ${
            isCopySuccess
              ? "success-state"
              : !canCopyLink || isSending || isUrlLoading || isDomainToggleBusy
              ? "disabled-look"
              : "outlined-state"
          }`}
          onClick={() => handleCopyLink()}
          disabled={
            isCopySuccess ||
            !canCopyLink ||
            isSending ||
            isDomainToggleBusy ||
            isRevokeLocked ||
            isUrlLoading
          }
        >
          <img
            src={copyLink}
            alt="copy"
            className={`copy-link-icon ${
              isCopySuccess
                ? "icon-white"
                : !canCopyLink || isSending || isUrlLoading || isDomainToggleBusy
                ? "icon-disabled"
                : "icon-purple"
            }`}
          />
          {isCopySuccess ? "Link copied to clipboard" : "Copy Link"}
        </button>
      </Box>
    </Dialog>

    <ProgressSpinner showSpinner={showSpinner} />
    </>
  );
};

export default ShareInteractionDialog;
